/* ============================================================================================
   SlimTag Buddy buy box — unified selection controller
   --------------------------------------------------------------------------------------------
   One product (slimtag-buddy) has two option dimensions:
     • Konnektivität  -> rendered as a button group ("Apple" / "Apple/Android")
     • Motiv          -> rendered as a native <select> (Affe, Alpaka, Einhorn, …)

   Three pieces used to fight over the variant selection:
     1. The theme's <variant-selects>.setAvailability() — disables/re-enables every <option> on
        each change. Harmless intent, but it throws when we remove options (Safari filtering) and
        drops the selected value mid-pass (Firefox price flash).
     2. The theme's <media-gallery> "slider_controls_selection" — when a slide becomes active it
        maps the image to the FIRST matching variant (the Apple one for a shared motif image) and
        overwrites BOTH selects, resetting connectivity (the "shows Apple/Android but adds Apple"
        bug).
     3. This buy box — wants the connectivity buttons to be authoritative and the motif select to
        be filtered per connectivity.

   This controller makes the buy box the SINGLE owner of variant selection. It neutralizes (1) and
   (2) on our instances and reimplements the useful half of (2) — "scroll the gallery to select a
   motif" — in a connectivity-aware way. Because it's all driven from JS, it no longer matters
   whether the section's "slider_controls_selection" setting is on or off.

   The native <select>s remain the source of truth, so the theme's variant resolution (price, ATC,
   variant:change, upsell, gallery-follows-selection) keeps working untouched.
   ============================================================================================ */
(function () {
  'use strict';

  var NOOP = function () {};

  function initBuyBox(root) {
    /* ---- 1. Resolve the core elements ------------------------------------------------------ */
    var vs = root.querySelector('variant-selects');
    if (!vs) return; // single-option product (e.g. before the admin variant setup) — nothing to do

    var jsonEl = vs.querySelector('script[type="application/json"]');
    if (!jsonEl) return;

    var variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(variants)) return;

    var selects = Array.prototype.slice.call(vs.querySelectorAll('select'));
    if (selects.length < 2) return; // need both option dimensions present

    var connWrapper = vs.querySelector('[data-connectivity-wrapper]');
    var connSelect = connWrapper ? connWrapper.querySelector('select') : null;
    if (!connSelect) return;

    var motifSelect = selects.filter(function (s) { return s !== connSelect; })[0];
    if (!motifSelect) return;

    // Positional option indices (DOM select order == Shopify option order == variant.options order).
    var connIndex = selects.indexOf(connSelect);
    var motifIndex = selects.indexOf(motifSelect);

    var featured = root.closest('.featured-product');
    var mediaGallery = featured ? featured.querySelector('media-gallery') : null;

    /* ---- 2. Precompute every lookup ONCE, upfront ------------------------------------------
       All variant combinations are already embedded in the page (the variant-selects JSON), so
       no network request is needed — filtering and slide-mapping are instant. */
    var motifMaster = Array.prototype.map.call(motifSelect.options, function (o) {
      return { value: o.value, label: o.textContent };
    });

    // connectivity value -> ordered list of motifs that have a real variant for it
    var motifsByConnectivity = {};
    Array.prototype.forEach.call(connSelect.options, function (c) {
      var present = {};
      variants.forEach(function (v) {
        if (v.options[connIndex] === c.value) present[v.options[motifIndex]] = true;
      });
      motifsByConnectivity[c.value] = motifMaster.filter(function (m) { return present[m.value]; });
    });

    // featured_media id -> motif value (motif is identical across the connectivity variants that
    // share an image, so this is unambiguous even when several variants point to the same media)
    var motifByMediaId = {};
    variants.forEach(function (v) {
      if (v.featured_media && v.featured_media.id != null) {
        motifByMediaId[v.featured_media.id] = v.options[motifIndex];
      }
    });

    /* ---- 3. Take exclusive control of variant selection ------------------------------------ */
    // (a) Neutralize the theme's availability pass on THIS <variant-selects>. For a dropdown it
    //     only ever re-enables everything, so we lose nothing — but we drop its two side-effects
    //     (throws on removed options + value reset mid-pass). We own the option set ourselves.
    vs.setAvailability = NOOP;
    vs.setAvailabilityInUnavailable = NOOP;

    // (b) Neutralize the gallery's slider→variant coupling and its [variant] button text. We
    //     re-implement the slide→motif behaviour connectivity-aware in step 6. Doing this on the
    //     instance means the section's "slider_controls_selection" setting can be either value.
    if (mediaGallery) {
      mediaGallery.selectVariantProgrammatically = NOOP;
      mediaGallery.updateBuyButtonText = NOOP;
    }

    /* ---- 4. Selection primitives ----------------------------------------------------------- */
    // Ask the theme to resolve the variant from the current <select> values. Dispatching on the
    // <variant-selects> element itself triggers its own change listener (price, ATC, URL,
    // variant:change -> upsell + gallery-follows-selection).
    // The gallery animates to follow EVERY selection change and re-emits slider:change "echoes"
    // for the slides it passes through. Those are not user navigation; reacting to them creates a
    // feedback loop (selection -> gallery -> echo -> selection, which flips connectivity back and
    // reverts the price). So we ignore slide handling for a short window around each programmatic
    // change. The window is opened BEFORE we dispatch (covers synchronous echoes) and also via a
    // capture-phase change listener on <variant-selects> (covers the native motif-dropdown path).
    var GALLERY_ECHO_MS = 800;
    var ignoreSlidesUntil = 0;
    function now() { return (window.performance && performance.now) ? performance.now() : Date.now(); }
    function openEchoWindow() { ignoreSlidesUntil = now() + GALLERY_ECHO_MS; }

    function resolveVariant() {
      openEchoWindow();
      vs.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Native motif-dropdown changes bubble straight to <variant-selects>; catch them in the capture
    // phase (before the theme's resolution + gallery scroll) so their echoes are suppressed too.
    vs.addEventListener('change', openEchoWindow, true);

    // Keep a <select is="custom-select"> styling attribute in sync without bubbling to the
    // <variant-selects> (avoids a second, redundant resolve).
    function syncCustomSelect(sel) {
      sel.dispatchEvent(new Event('change'));
    }

    /* ---- 5. Connectivity-aware motif filtering --------------------------------------------
       Rebuild the motif <select> to ONLY the motifs available for a connectivity. Removing
       options (not hiding) is the only filter every browser honours — Safari ignores
       <option hidden>. Safe now that setAvailability no longer walks the option set.
       Preserves the current motif if still valid, else falls back to the first valid one.
       Does NOT resolve — the caller resolves once. */
    function filterMotifOptions(connValue) {
      var list = motifsByConnectivity[connValue] || motifMaster;
      var current = motifSelect.value;
      var keepCurrent = false;
      for (var i = 0; i < list.length; i++) {
        if (list[i].value === current) { keepCurrent = true; break; }
      }

      motifSelect.innerHTML = '';
      list.forEach(function (m) {
        var opt = document.createElement('option');
        opt.value = m.value;
        opt.textContent = m.label;
        motifSelect.appendChild(opt);
      });

      motifSelect.value = keepCurrent ? current : (list.length ? list[0].value : '');
      syncCustomSelect(motifSelect);
    }

    function motifAvailableFor(connValue, motifValue) {
      var list = motifsByConnectivity[connValue] || motifMaster;
      for (var i = 0; i < list.length; i++) {
        if (list[i].value === motifValue) return true;
      }
      return false;
    }

    // First connectivity (in button order) that actually offers a given motif.
    function findConnectivityForMotif(motifValue) {
      var opts = connSelect.options;
      for (var i = 0; i < opts.length; i++) {
        if (motifAvailableFor(opts[i].value, motifValue)) return opts[i].value;
      }
      return null;
    }

    /* ---- 6. Connectivity button group ------------------------------------------------------ */
    var group = document.createElement('div');
    group.className = 'kk-buddy-connectivity';
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', root.getAttribute('data-connectivity-option') || 'Konnektivität');

    function setActiveButton(value) {
      Array.prototype.forEach.call(group.children, function (b) {
        var on = b.getAttribute('data-value') === value;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
    }

    // Choose a connectivity: update the (hidden) select, the active button, the motif options,
    // then resolve once.
    function selectConnectivity(value) {
      connSelect.value = value;
      setActiveButton(value);
      filterMotifOptions(value);
      resolveVariant();
    }

    Array.prototype.forEach.call(connSelect.options, function (o) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'kk-buddy-connectivity__btn';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('data-value', o.value);
      btn.textContent = o.textContent.trim();
      // Guard on the button's own visual state — the buttons are the UI; the native <select> is a
      // hidden implementation detail. Idempotent and robust to any external value change.
      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-active')) return;
        selectConnectivity(o.value);
      });
      group.appendChild(btn);
    });

    // Place the buttons above all option selects so connectivity is chosen first, independent of
    // the product's Shopify option order. The native <select>s stay in option order.
    var firstOption = vs.querySelector('.kk-buddy-buy-box__option');
    vs.insertBefore(group, firstOption || connWrapper);
    root.classList.add('kk-buddy-buy-box--enhanced');

    /* ---- 7. Gallery slide -> motif (connectivity-aware) -----------------------------------
       Scrolling the gallery selects the matching MOTIF. If the current connectivity offers that
       motif, connectivity is kept. If it does NOT (e.g. on "Apple/Android" the user scrolls to an
       Apple-only design), we switch connectivity to the first one that offers the motif, so the
       gallery and the selection never disagree. The gallery follows the selection in the other
       direction via the theme's MediaGallery.onVariantChanged. */
    function onGallerySlideChange(event) {
      if (now() < ignoreSlidesUntil) return; // gallery echo from our own change, not user navigation
      var media = event && event.detail ? event.detail.currentElement : null;
      if (!media) return;

      var mediaId = parseInt(media.getAttribute('data-media-id'), 10);
      if (!mediaId) return;

      var motif = motifByMediaId[mediaId];
      if (!motif) return; // image has no associated variant

      // Motif is offered for the current connectivity -> just switch motif, keep connectivity.
      if (motifAvailableFor(connSelect.value, motif)) {
        if (motif === motifSelect.value) return; // already selected
        motifSelect.value = motif;
        syncCustomSelect(motifSelect);
        resolveVariant();
        return;
      }

      // Current connectivity has no variant for this motif -> switch to one that does, then select
      // the motif there. (e.g. slide to "Affe" while on Apple/Android -> switch back to "Apple".)
      var targetConn = findConnectivityForMotif(motif);
      if (!targetConn || targetConn === connSelect.value) return;

      connSelect.value = targetConn;
      setActiveButton(targetConn);
      filterMotifOptions(targetConn); // rebuild the motif list for the new connectivity
      motifSelect.value = motif;       // now valid for targetConn
      syncCustomSelect(motifSelect);
      resolveVariant();
    }

    if (mediaGallery) {
      var slider = mediaGallery.querySelector('slider-element');
      if (slider) slider.addEventListener('slider:change', onGallerySlideChange);
    }

    /* ---- 8. Dynamic add-to-cart button text ("Buddy <Motiv> hinzufügen") ------------------- */
    var template = root.getAttribute('data-add-button-template');
    var submitTextEl = root.querySelector('.product-form__submit .btn-text');

    function applyButtonText() {
      if (!template || !submitTextEl) return;
      var variant = vs.currentVariant;
      if (!variant || variant.available === false) return; // keep the theme's sold-out text
      submitTextEl.textContent = template.replace(/\[motiv\]/g, motifSelect.value);
    }

    /* ---- 9. Initial state ------------------------------------------------------------------
       The server already rendered the correct default variant, so we only sync the UI (active
       button + filtered motif list + button text) — no resolve, no extra fetch. */
    setActiveButton(connSelect.value);
    filterMotifOptions(connSelect.value);
    applyButtonText();

    // renderProductInfo() fires productInfo:loaded as its final step (after toggleBuyButton has
    // reset the label), so re-applying here reliably wins.
    document.addEventListener('productInfo:loaded', applyButtonText);
  }

  function boot() {
    var roots = document.querySelectorAll('[data-kk-buddy-buy-box]');
    Array.prototype.forEach.call(roots, initBuyBox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
